import { FontFamily, FontSize } from "config/font";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarHeader,
  CalendarIcon,
  CalendarPopup,
  DatePickerContainer,
  DatePickerInput,
  DatePickerLabel,
  DayCell,
  DayHeader,
  DaysGrid,
  MonthYear,
  NavButton,
  SelectedDate,
} from "./datePickerWithLabel.styled";
import { PlaceholderText } from "../dropDownWithLabel/dropDownWithLabel.styled";
import { EyeIcon } from "../textInput/textInput.styled";
import { Eye } from "../textInput/textInput.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import { Color } from "config/colors";

type SelectionMode = "single" | "range";

export interface DatePickerWithLabelProps {
  label: string;
  value?: Date | null;
  allowClear?: boolean;
  monthlySelectableDay?: number;
  allowRangePicker?: boolean;
  excludeNonWorkingDays?: boolean;
  onExcludeNonWorkingDaysChange?: (checked: boolean) => void;
  excludeHolidays?: boolean;
  onExcludeHolidaysChange?: (checked: boolean) => void;
  excludeWeekends?: boolean;
  onExcludeWeekendsChange?: (checked: boolean) => void;
  required?: boolean;
  hasError?: boolean;
  fontSize?: FontSize;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (date: Date | null) => void;
  onRangeChange?: (dates: Date[]) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  dateFormat?: "YYYY-MM-DD" | "DD/MM/YYYY" | "MM/DD/YYYY";
  earliestSelectableDate?: Date;
  restrictToMonth?: Date;
  disableNavigationOutsideMonth?: boolean;

  rangeOnly?: boolean;
  showExcludeToggles?: boolean;
}

export const CalendarIconSVG = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

export const DatePickerWithLabel: React.FC<DatePickerWithLabelProps> = (
  props
) => {
  const allowRange = props.allowRangePicker ?? false;
  const rangeOnly = !!props.rangeOnly;
  const showExcludeToggles = props.showExcludeToggles !== false;
  const effectiveRange = allowRange || rangeOnly;
  const initialMode: SelectionMode = rangeOnly ? "range" : "single";
  const [mode, setMode] = useState<SelectionMode>(initialMode);
  const [isOpen, setIsOpen] = useState(false);

  const allowClear = props.allowClear === true;

  const monthlyDay =
    typeof props.monthlySelectableDay === "number" &&
    props.monthlySelectableDay >= 1 &&
    props.monthlySelectableDay <= 31
      ? props.monthlySelectableDay
      : null;

  const matchesMonthlyDay = (d: Date): boolean => {
    if (!monthlyDay) return true;
    return d.getDate() === monthlyDay;
  };

  const normalizeToFirstOfMonth = (base: Date) =>
    new Date(base.getFullYear(), base.getMonth(), 1);

  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    normalizeToFirstOfMonth(props.value ?? new Date())
  );

  useEffect(() => {
    if (props.value instanceof Date) {
      setCurrentMonth(normalizeToFirstOfMonth(props.value));
    }
  }, [props.value]);

  const [companyState] = useCompanyStore();

  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [range, setRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });
  const [multiSelectedKeys, setMultiSelectedKeys] = useState<Set<string>>(
    new Set()
  );

  const [committedRange, setCommittedRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [committedMultiKeys, setCommittedMultiKeys] = useState<Set<string>>(
    new Set()
  );

  const datePickerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const startOfDay = (d: Date | null | undefined) => {
    if (!d) return null;
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const minDate = useMemo(
    () => startOfDay(props.earliestSelectableDate),
    [props.earliestSelectableDate]
  );
  const isBeforeMin = (d: Date) => {
    if (!minDate) return false;
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x < minDate;
  };
  const isAllowedByMin = (d: Date) => !isBeforeMin(d);

  const restrictSameMonthRange = props.restrictToMonth != null;
  const isSameMonthYear = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

  const isWeekend = (d: Date) => {
    const dow = d.getDay();
    return dow === 0 || dow === 6;
  };

  const [nonWorkSet, setNonWorkSet] = useState<Set<string>>(new Set());
  const companyId = companyState.company?.id ?? null;
  const year = currentMonth.getFullYear();

  const isControlledHoliday = typeof props.excludeHolidays === "boolean";
  const isControlledWeekends = typeof props.excludeWeekends === "boolean";

  const [localExcludeHolidays, setLocalExcludeHolidays] = useState(false);
  const [localExcludeWeekends, setLocalExcludeWeekends] = useState(false);

  const legacyExclude = !!props.excludeNonWorkingDays;

  const excludeHolidaysValue =
    (isControlledHoliday ? !!props.excludeHolidays : localExcludeHolidays) ||
    legacyExclude;

  const excludeWeekendsValue =
    (isControlledWeekends ? !!props.excludeWeekends : localExcludeWeekends) ||
    legacyExclude;

  const fmtKey = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    if (!companyId) return;

    const includeWeekends = !!excludeWeekendsValue;

    (async () => {
      try {
        const res = await Api().company.gig.getNonWorkDays(
          companyId,
          year,
          includeWeekends
        );
        const list = Array.isArray(res)
          ? res
          : Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];

        const keys = list
          .map((item: any) => {
            const d = item instanceof Date ? item : new Date(item as string);
            return isNaN(d.getTime()) ? null : fmtKey(d);
          })
          .filter((k: any): k is string => Boolean(k));

        setNonWorkSet(new Set(keys));
      } catch {}
    })();
  }, [companyId, year, excludeWeekendsValue]);

  const isRedDayFromApi = (d: Date) => nonWorkSet.has(fmtKey(d));

  const shouldExclude = (d: Date): boolean => {
    if (excludeWeekendsValue && isWeekend(d)) return true;
    if (excludeHolidaysValue && isRedDayFromApi(d)) return true;
    return false;
  };

  const buildDateArray = (start: Date, end: Date): Date[] => {
    const arr: Date[] = [];
    let d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const endCopy = new Date(end);
    endCopy.setHours(0, 0, 0, 0);

    while (d <= endCopy) {
      if (
        isAllowedByMin(d) &&
        !shouldExclude(d) &&
        matchesMonthlyDay(d) 
      ) {
        arr.push(new Date(d));
      }
      d.setDate(d.getDate() + 1);
    }
    return arr;
  };

  const handleDateSelectSingleImmediate = (date: Date) => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return; 
    props.onChange?.(date);
    setIsOpen(false);
    props.onBlur?.();
  };

  const handleDateSelectRange = (date: Date) => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return; 

    if (!pendingStart) {
      setPendingStart(date);
      setRange({ start: date, end: null });
      return;
    }

    if (restrictSameMonthRange && !isSameMonthYear(date, pendingStart)) {
      setPendingStart(date);
      setRange({ start: date, end: null });
      return;
    }

    const start = pendingStart;
    let end = date;
    if (end < start) {
      const tmp = end;
      end = start;
      setRange({ start: tmp, end });
    } else {
      setRange({ start, end });
    }
    setPendingStart(null);
  };

  const days = useMemo(() => {
    const base = currentMonth;
    const y = base.getFullYear();
    const month = base.getMonth();

    const firstDay = new Date(y, month, 1);
    const lastDay = new Date(y, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const mondayBasedStartDay =
      startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    const out: { date: Date; isOtherMonth: boolean }[] = [];
    const prevMonth = new Date(y, month, 0);

    for (let i = mondayBasedStartDay - 1; i >= 0; i--) {
      out.push({
        date: new Date(y, month - 1, prevMonth.getDate() - i),
        isOtherMonth: true,
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      out.push({ date: new Date(y, month, i), isOtherMonth: false });
    }
    const remaining = 42 - out.length;
    for (let i = 1; i <= remaining; i++) {
      out.push({ date: new Date(y, month + 1, i), isOtherMonth: true });
    }
    return out;
  }, [currentMonth]);

  const monthNames = [
    t("OrganizationalStructure.January"),
    t("OrganizationalStructure.February"),
    t("OrganizationalStructure.March"),
    t("OrganizationalStructure.April"),
    t("OrganizationalStructure.May"),
    t("OrganizationalStructure.June"),
    t("OrganizationalStructure.July"),
    t("OrganizationalStructure.August"),
    t("OrganizationalStructure.September"),
    t("OrganizationalStructure.October"),
    t("OrganizationalStructure.November"),
    t("OrganizationalStructure.December"),
  ];

  const dayHeaders = [
    t("OrganizationalStructure.Mo"),
    t("OrganizationalStructure.Tu"),
    t("OrganizationalStructure.We"),
    t("OrganizationalStructure.Th"),
    t("OrganizationalStructure.Fr"),
    t("OrganizationalStructure.Sa"),
    t("OrganizationalStructure.Su"),
  ];

  const today = new Date();

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    switch (props.dateFormat) {
      case "DD/MM/YYYY":
        return `${d}/${m}/${y}`;
      case "MM/DD/YYYY":
        return `${m}/${d}/${y}`;
      default:
        return `${y}-${m}-${d}`;
    }
  };

  const parseKey = (key: string) => {
    const [y, m, d] = key.split("-").map((x) => parseInt(x, 10));
    return new Date(y, m - 1, d);
  };

  const formatDatesList = (dates: Date[]) => {
    const formatted = dates.map((d) => formatDate(d));
    if (formatted.length <= 3) return formatted.join(", ");
    return `${formatted.slice(0, 3).join(", ")} +${formatted.length - 3}`;
  };

  const getDraftDisplay = (): string => {
    if (!effectiveRange) return props.value ? formatDate(props.value) : "";
    if (mode === "range") {
      return range.start && range.end
        ? `${formatDate(range.start)} – ${formatDate(range.end)}`
        : range.start
        ? `${formatDate(range.start)} – …`
        : "";
    } else {
      const keys = Array.from(multiSelectedKeys);
      if (keys.length === 0) return "";
      const dates = keys.map(parseKey).sort((a, b) => +a - +b);
      return formatDatesList(dates);
    }
  };

  const getCommittedDisplay = (): string => {
    if (!effectiveRange) return props.value ? formatDate(props.value) : "";
    if (mode === "range") {
      return committedRange.start && committedRange.end
        ? `${formatDate(committedRange.start)} – ${formatDate(
            committedRange.end
          )}`
        : "";
    } else {
      const keys = Array.from(committedMultiKeys);
      if (keys.length === 0) return "";
      const dates = keys.map(parseKey).sort((a, b) => +a - +b);
      return formatDatesList(dates);
    }
  };

  const showValue = isOpen ? getDraftDisplay() : getCommittedDisplay();

  const canConfirm = useMemo(() => {
    if (mode === "range") {
      return !!(range.start && range.end);
    }
    if (effectiveRange) {
      return multiSelectedKeys.size > 0 || !!props.value;
    }
    return !!props.value;
  }, [mode, range, effectiveRange, multiSelectedKeys, props.value]);

  const handleConfirm = () => {
    if (props.disabled || !canConfirm) return;

    let selectedDates: Date[] = [];

    if (mode === "range" && range.start && range.end) {
      selectedDates = buildDateArray(range.start, range.end);
    } else if (effectiveRange) {
      const keys = Array.from(multiSelectedKeys);
      const datesFromKeys =
        keys.length > 0 ? keys.map(parseKey).sort((a, b) => +a - +b) : [];
      const base =
        datesFromKeys.length > 0
          ? datesFromKeys
          : props.value
          ? [props.value]
          : [];
      selectedDates = base.filter((d) => {
        if (!isAllowedByMin(d)) return false;
        if (shouldExclude(d)) return false;
        if (!matchesMonthlyDay(d)) return false; 
        return true;
      });
    } else {
      selectedDates = [];
    }

    if (mode === "range") {
      if (range.start && range.end) {
        setCommittedRange({ start: range.start, end: range.end });
        setCommittedMultiKeys(new Set());
      }
    } else if (effectiveRange) {
      const keys = new Set(selectedDates.map((d) => fmtKey(d)));
      setCommittedMultiKeys(keys);
      setCommittedRange({ start: null, end: null });
    }

    if (props.onRangeChange) {
      props.onRangeChange(selectedDates);
    } else if (!effectiveRange) {
      props.onChange?.(selectedDates[0] ?? null);
    }

    setIsOpen(false);
    props.onBlur?.();
  };

  const handleClearCommitted = (e?: React.MouseEvent) => {
    if (!allowClear) return;
    e?.stopPropagation();
    if (props.disabled) return;

    setPendingStart(null);
    setRange({ start: null, end: null });
    setMultiSelectedKeys(new Set());
    setCommittedRange({ start: null, end: null });
    setCommittedMultiKeys(new Set());

    if (effectiveRange) {
      if (props.onRangeChange) props.onRangeChange([]);
      else props.onChange?.(null);
    } else {
      props.onChange?.(null);
    }

    setIsOpen(false);
    props.onBlur?.();
  };

  useEffect(() => {
    if (rangeOnly) setMode("range");
  }, [rangeOnly]);

  useEffect(() => {
    if (isOpen) {
      setRange({ ...committedRange });
      setMultiSelectedKeys(new Set(committedMultiKeys));
      setPendingStart(null);
    }
  }, [isOpen, committedRange, committedMultiKeys]);

  const showModeButtons = effectiveRange && !rangeOnly;
  const showHolidayWeekendToggles = mode === "range" && showExcludeToggles;

  const showClearButton = allowClear && !props.disabled && !!showValue;

  return (
    <DatePickerContainer ref={datePickerRef}>
      <DatePickerLabel required={props.required} disabled={props.disabled}>
        {props.label}
      </DatePickerLabel>

      <DatePickerInput
        hasError={props.hasError}
        fontSize={props.fontSize}
        isOpen={isOpen}
        disabled={props.disabled}
        onClick={() => {
          if (!props.disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) props.onFocus?.();
          }
        }}
        style={{ position: "relative" }}
      >
        {showValue ? (
          <SelectedDate>{showValue}</SelectedDate>
        ) : (
          <PlaceholderText>{props.placeholder}</PlaceholderText>
        )}

        {showClearButton && (
          <button
            type="button"
            onClick={handleClearCommitted}
            aria-label={t("DatePicker.Clear")}
            style={{
              position: "absolute",
              right: 36,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
              padding: 4,
              color: "#667085",
            }}
          >
            ×
          </button>
        )}

        {props.disabled ? (
          <EyeIcon>
            <Eye size={16} />
          </EyeIcon>
        ) : (
          <CalendarIcon>
            <CalendarIconSVG />
          </CalendarIcon>
        )}
      </DatePickerInput>

      {!props.disabled && (
        <CalendarPopup isOpen={isOpen}>
          {showModeButtons && (
            <div
              style={{
                display: "flex",
                gap: 8,
                padding: "8px 8px 0 8px",
                marginBottom: 15,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setMode("single");
                  setPendingStart(null);
                  setRange({ start: null, end: null });
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "1px solid #D0D5DD",
                  background: mode === "single" ? "#e6f4ea" : "white",
                  fontFamily: FontFamily.MontserratRegular,
                  fontSize: FontSize.Small,
                }}
              >
                {t("DatePicker.Single")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("range");
                  setPendingStart(null);
                  setRange({ start: null, end: null });
                  setMultiSelectedKeys(new Set());
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "1px solid #D0D5DD",
                  background: mode === "range" ? "#e6f4ea" : "white",
                  fontFamily: FontFamily.MontserratRegular,
                  fontSize: FontSize.Small,
                }}
              >
                {t("DatePicker.Range")}
              </button>
            </div>
          )}

          {showHolidayWeekendToggles && (
            <>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px 0 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                  fontFamily: `${FontFamily.MontserratRegular}`,
                  fontSize: `${FontSize.Small}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!excludeHolidaysValue}
                  onChange={() => {
                    const next = isControlledHoliday
                      ? !props.excludeHolidays
                      : !localExcludeHolidays;

                    if (!isControlledHoliday) {
                      setLocalExcludeHolidays(next);
                    }
                    props.onExcludeHolidaysChange?.(!!next);
                    props.onExcludeNonWorkingDaysChange?.(
                      !!next || !!excludeWeekendsValue
                    );
                  }}
                />
                {t("DatePicker.ExcludeHolidays")}
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 12px 8px 12px",
                  marginBottom: 12,
                  cursor: "pointer",
                  fontFamily: `${FontFamily.MontserratRegular}`,
                  fontSize: `${FontSize.Small}`,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!excludeWeekendsValue}
                  onChange={() => {
                    const next = isControlledWeekends
                      ? !props.excludeWeekends
                      : !localExcludeWeekends;

                    if (!isControlledWeekends) {
                      setLocalExcludeWeekends(next);
                    }
                    props.onExcludeWeekendsChange?.(!!next);
                    props.onExcludeNonWorkingDaysChange?.(
                      !!excludeHolidaysValue || !!next
                    );
                  }}
                />
                {t("DatePicker.ExcludeSaturdayAndSunday")}
              </label>
            </>
          )}

          <CalendarHeader>
            <NavButton
              onClick={() => {
                const base = currentMonth;
                const prev = new Date(
                  base.getFullYear(),
                  base.getMonth() - 1,
                  1
                );
                setCurrentMonth(prev);
              }}
            >
              ‹
            </NavButton>
            <MonthYear>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </MonthYear>
            <NavButton
              onClick={() => {
                const base = currentMonth;
                const next = new Date(
                  base.getFullYear(),
                  base.getMonth() + 1,
                  1
                );
                setCurrentMonth(next);
              }}
            >
              ›
            </NavButton>
          </CalendarHeader>

          <DaysGrid>
            {dayHeaders.map((day) => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {days.map((day, idx) => {
              const cellDate = day.date;
              const isToday = cellDate.toDateString() === today.toDateString();

              const isSelected =
                mode === "range"
                  ? (() => {
                      const { start, end } = range;
                      if (start && end) {
                        const d = new Date(cellDate);
                        d.setHours(0, 0, 0, 0);
                        const s = new Date(start);
                        s.setHours(0, 0, 0, 0);
                        const e = new Date(end);
                        e.setHours(0, 0, 0, 0);
                        return d >= s && d <= e;
                      }
                      if (pendingStart) {
                        return (
                          cellDate.toDateString() ===
                          pendingStart.toDateString()
                        );
                      }
                      return false;
                    })()
                  : effectiveRange
                  ? multiSelectedKeys.has(fmtKey(cellDate))
                  : !!(
                      props.value &&
                      cellDate.toDateString() === props.value.toDateString()
                    );

              const outsideStartMonthBlock =
                restrictSameMonthRange &&
                mode === "range" &&
                !!pendingStart &&
                !isSameMonthYear(cellDate, pendingStart);

              const disabledByMonthlyDay = !matchesMonthlyDay(cellDate);

              const disabledBool =
                outsideStartMonthBlock ||
                !isAllowedByMin(cellDate) ||
                shouldExclude(cellDate) ||
                day.isOtherMonth ||
                disabledByMonthlyDay; // ✅ ny regel

              const onClickDay = () => {
                if (disabledBool) return;
                if (mode === "range") {
                  handleDateSelectRange(cellDate);
                } else {
                  if (effectiveRange) {
                    const key = fmtKey(cellDate);
                    setMultiSelectedKeys((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      return next;
                    });
                  } else {
                    handleDateSelectSingleImmediate(cellDate);
                  }
                }
              };

              return (
                <DayCell
                  key={idx}
                  isToday={!!isToday}
                  isSelected={!!isSelected}
                  isOtherMonth={!!day.isOtherMonth}
                  onClick={onClickDay}
                  aria-disabled={disabledBool ? "true" : "false"}
                  title={
                    disabledBool ? t("DatePicker.NotSelectable") : undefined
                  }
                  style={
                    disabledBool
                      ? { opacity: 0.45, cursor: "not-allowed" }
                      : undefined
                  }
                >
                  {cellDate.getDate()}
                </DayCell>
              );
            })}
          </DaysGrid>

          {effectiveRange && (
            <div
              style={{
                display: "flex",
                gap: 8,
                padding: "8px 12px 12px 12px",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                zIndex: 2,
              }}
            >
              {allowClear ? (
                <button
                  type="button"
                  onClick={handleClearCommitted}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "1px solid #D0D5DD",
                    background: "white",
                    fontFamily: FontFamily.MontserratRegular,
                    fontSize: FontSize.Small,
                  }}
                >
                  {t("DatePicker.Clear")}
                </button>
              ) : (
                <div />
              )}

              <button
                type="button"
                onClick={handleConfirm}
                disabled={!canConfirm}
                style={{
                  padding: "8px 16px",
                  borderRadius: 10,
                  border: "1px solid transparent",
                  background: canConfirm ? Color.ModernGreen : "#D0D5DD",
                  color: canConfirm ? "white" : "#667085",
                  fontFamily: FontFamily.MontserratRegular,
                  fontSize: FontSize.Small,
                  boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                  cursor: canConfirm ? "pointer" : "not-allowed",
                }}
                aria-disabled={canConfirm ? "false" : "true"}
              >
                {t("DatePicker.Choose")}
              </button>
            </div>
          )}
        </CalendarPopup>
      )}
    </DatePickerContainer>
  );
};